import axios from 'axios';

const API_URL = 'https://plotgoblin.com/api';
// const API_URL = 'http://localhost:5001/api'; 

const getApiKey = () => localStorage.getItem('openRouterApiKey');

const getDialogueRatio = (text) => {
  const dialogueMatches = [...text.matchAll(/"([^"]+)"/g)];
  
  let dialogueWordCount = 0;
  dialogueMatches.forEach(match => {
      dialogueWordCount += match[1].split(/\s+/).filter(word => word.length > 0).length;
  });

  const totalWordCount = text.split(/\s+/).filter(word => word.length > 0).length;
  const nonDialogueWordCount = totalWordCount - dialogueWordCount;

  return {
      dialogueWords: dialogueWordCount,
      nonDialogueWords: nonDialogueWordCount,
      dialoguePercentage: ((dialogueWordCount / totalWordCount) * 100).toFixed(2) + "%",
      nonDialoguePercentage: ((nonDialogueWordCount / totalWordCount) * 100).toFixed(2) + "%"
  };
}

export const api = {
  generateLoglines: async (preferences = '', count = 5) => {
    const user = JSON.parse(localStorage.getItem('user'));
    const userId = user.id;

    const apiKey = getApiKey();
    const response = await axios.post(`${API_URL}/loglines/generate?userId=${userId}}`, { 
      apiKey,
      userId,
      preferences,
      count
    });
    return response.data;
  },
  
  createNovel: async (novelData) => {
    const user = JSON.parse(localStorage.getItem('user'));
    const userId = user.id;

    const sanitizedData = {
      ...novelData,
      title: String(novelData.title),
      type: String(novelData.type),
      logline: String(novelData.logline),
      writingPreferences: novelData.writingPreferences,
      userId
    };
    
    try {
      const response = await axios.post(`${API_URL}/novels/create`, sanitizedData);
      return response.data;
    } catch (error) {
      console.error('Error creating novel:', error);
      throw error;
    }
  },
  
  authenticateWithGoogle: async (userData) => {
    try {
      const response = await axios.post(`${API_URL}/auth/google`, {
        email: userData.email,
        name: userData.name,
        picture: userData.picture,
        google_id: userData.sub
      });

      return response.data;
    } catch (error) {
      console.error('Error during authentication:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Authentication failed'
      };
    }
  },
  
  getAllNovels: async () => {
    const user = JSON.parse(localStorage.getItem('user'));
    const userId = user.id;
    const response = await axios.get(`${API_URL}/novels?userId=${userId}`);
    return response.data;
  },
  
  getNovelById: async (id) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;
    
    const response = await axios.get(`${API_URL}/novels/${id}?userId=${userId}`);

    if (response.data.success && response.data.novel && response.data.novel.chapters) {
      response.data.novel.chapters.forEach(chapter => {
        chapter.words = 0;
        if (chapter.scenes && Array.isArray(chapter.scenes)) {
          chapter.scenes.forEach(scene => {
            if (scene.content) {
              const wordCount = scene.content.split(/\s+/).filter(Boolean).length;
              scene.words = wordCount;
              chapter.words += wordCount;

              scene.dialogeStats = getDialogueRatio(scene.content);
              
              scene.reading_time_mins = Math.round(wordCount / 238);
            } else {
              scene.words = 0;
              scene.reading_time_mins = 0;
            }
          });
        }
        chapter.reading_time_mins = Math.round(chapter.words / 238);
      });
      response.data.novel.reading_time_mins = Math.round(response.data.novel.word_count/ 238);
    }

    return response.data;
  },
  
  generateCharacters: async (novelId, characters) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;

    const apiKey = getApiKey();
    const response = await axios.post(`${API_URL}/characters/generate?userId=${userId}`, { 
      apiKey,
      novelId, 
      characters 
    });
    return response.data;
  },

  updateCharacter: async (character) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;

    const response = await axios.put(`${API_URL}/characters/update?userId=${userId}`, character);
    return response.message;
  },

  saveCharacters: async (novelId, characters) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;

    const response = await axios.post(`${API_URL}/characters/save?userId=${userId}`, { 
      novelId, 
      characters 
    });
    return response.data;
  },
  
  getCharactersByNovelId: async (novelId) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;

    const response = await axios.get(`${API_URL}/characters/novel/${novelId}?userId=${userId}`);
    return response.data;
  },
  
  deleteCharacter: async (characterId) => {
    const response = await axios.delete(`${API_URL}/characters/${characterId}`);
    return response.data;
  },
  
  createCharacter: async (novelId, characterData) => {
    const response = await axios.post(`${API_URL}/characters/create`, {
      novelId,
      character: characterData
    });
    return response.data;
  },
  
  generateChapters: async (novelId, chapterCount = 11) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;

    const apiKey = getApiKey();
    console.log(`Generating ${chapterCount} chapters for novel ${novelId}`);
    const response = await axios.post(`${API_URL}/chapters/generate?userId=${userId}`, { 
      apiKey,
      novelId,
      chapterCount
    });
    return response.data;
  },
  
  saveChapters: async (novelId, chapters) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;

    const response = await axios.post(`${API_URL}/chapters/save?userId=${userId}`, { 
      novelId, 
      chapters 
    });
    return response.data;
  },
  
  getChaptersByNovelId: async (novelId) => {
    const response = await axios.get(`${API_URL}/chapters/novel/${novelId}`);
    return response.data;
  },
  
  updateChapterContent: async (chapterId, content) => {
    const response = await axios.put(`${API_URL}/chapters/${chapterId}/content`, { 
      content 
    });
    return response.data;
  },
  
  generateScenes: async (novelId, chapterId) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;

    const apiKey = getApiKey();
    const response = await axios.post(`${API_URL}/scenes/generate?userId=${userId}`, { 
      apiKey,
      novelId, 
      chapterId 
    });
    return response.data;
  },
  
  saveScenes: async (novelId, chapterId, scenes, replace = true) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;

    const response = await axios.post(`${API_URL}/scenes/save?userId=${userId}`, { 
      novelId, 
      chapterId, 
      scenes,
      replace
    });
    return response.data;
  },
  
  getScenesByChapterId: async (chapterId) => {
    const response = await axios.get(`${API_URL}/scenes/chapter/${chapterId}`);
    return response.data;
  },
  
  generateSceneProse: async (novelId, chapterId, sceneId) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;
    const apiKey = getApiKey();

    const response = await axios.post(`${API_URL}/scenes/prose/generate?userId=${userId}`, { 
      apiKey,
      novelId, 
      chapterId,
      sceneId 
    });
    return response.data;
  },
  
  updateSceneProse: async (novelId, chapterId, sceneId, content) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;

    const response = await axios.put(`${API_URL}/scenes/${sceneId}/prose?userId=${userId}`, { 
      novelId, 
      chapterId,
      content 
    });
    return response.data;
  },
  
  updateSceneProseKeepSummary: async (novelId, chapterId, sceneId, content) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;

    const response = await axios.put(`${API_URL}/scenes/${sceneId}/prose-only?userId=${userId}`, { 
      novelId, 
      chapterId,
      content 
    });
    return response.data;
  },
  
  getAiEditSuggestion: async (novelId, chapterId, sceneId, currentProse, instruction, highlightedText = '') => {
    const apiKey = getApiKey();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id;

    const response = await axios.post(`${API_URL}/scenes/prose/ai-edit?userId=${userId}`, { 
      apiKey,
      novelId, 
      chapterId,
      sceneId,
      currentProse,
      instruction,
      highlightedText
    });
    return response.data;
  },
  
  updateSceneSummary: async (sceneId, summary) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;

    const response = await axios.put(`${API_URL}/scenes/${sceneId}/summary?userId=${userId}`, { 
      summary 
    });
    return response.data;
  },
  
  updateSceneDetails: async (sceneId, summary, pov) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;

    const response = await axios.put(`${API_URL}/scenes/${sceneId}/details?userId=${userId}`, { 
      summary,
      pov
    });
    return response.data;
  },

  deleteNovel: async (novelId) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;

    const response = await axios.delete(`${API_URL}/novels/${novelId}?userId=${userId}`);
    return response.data;
  },
  
  deleteScene: async (sceneId) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;
    
    const response = await axios.delete(`${API_URL}/scenes/${sceneId}?userId=${userId}`);
    return response.data;
  },

  deleteChapter: async (chapterId) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || 1;
      
      const response = await axios.delete(`${API_URL}/chapters/${chapterId}?userId=${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting chapter:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete chapter'
      };
    }
  },

  requestAIEdit: async (novelId, chapterId, sceneId, currentProse, instruction, highlightedText = '') => {
    try {
      const apiKey = getApiKey();
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id;
      
      const response = await axios.post(`${API_URL}/scenes/prose/ai-edit?userId=${userId}`, {
        apiKey,
        novelId,
        chapterId,
        sceneId,
        currentProse,
        instruction,
        highlightedText
      });
      
      return response.data;
    } catch (error) {
      console.error('Error requesting AI edit:', error);
      console.error('Error details:', error.response?.status, error.response?.statusText);
      if (error.response?.data) {
        console.error('Error response data:', error.response.data);
      }
      
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get AI editing suggestions'
      };
    }
  },

  getChatResponse: async (novelId, selectedContext, message, model, chatId = null) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;
    const apiKey = getApiKey();
    
    try {
      const response = await axios.post(`${API_URL}/chat/generate`, {
        apiKey,
        novelId,
        selectedContext,
        message,
        userId,
        chatId,
        model
      });
      return response.data;
    } catch (error) {
      console.error('Error getting chat response:', error);
      throw error;
    }
  },

  getChatsByNovel: async (novelId) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;
    
    try {
      const response = await axios.get(`${API_URL}/chat/novel/${novelId}?userId=${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching chats:', error);
      throw error;
    }
  },

  getChatById: async (chatId) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;
    
    try {
      const response = await axios.get(`${API_URL}/chat/${chatId}?userId=${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching chat:', error);
      throw error;
    }
  },

  deleteChat: async (chatId) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;
    
    try {
      const response = await axios.delete(`${API_URL}/chat/${chatId}?userId=${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting chat:', error);
      throw error;
    }
  },

  updateChatTitle: async (chatId, title) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;
    
    try {
      const response = await axios.put(`${API_URL}/chat/${chatId}/title`, {
        title,
        userId
      });
      return response.data;
    } catch (error) {
      console.error('Error updating chat title:', error);
      return { success: false, error: 'Failed to update chat title' };
    }
  },

  createEmptyChat: async (novelId) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || 1;
      
      const title = 'New Chat';
      const initialMessage = {
        role: 'system',
        message: 'This is the beginning of your chat. Ask a question about your story.',
        timestamp: new Date().toISOString()
      };
      
      const response = await axios.post(`${API_URL}/chat/create`, {
        userId,
        novelId,
        title,
        messages: [initialMessage]
      });
      
      return response.data;
    } catch (error) {
      console.error('Error creating new chat:', error);
      throw error;
    }
  },

  createEmptyChapter: async (novelId) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;
    
    try {
      const response = await axios.post(`${API_URL}/chapters/create?userId=${userId}`, { 
        novelId 
      });
      return response.data;
    } catch (error) {
      console.error("Error creating empty chapter:", error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create chapter'
      };
    }
  },

  getLocationsByNovelId: async (novelId) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || 1;

      const response = await axios.get(`${API_URL}/locations/novel/${novelId}?userId=${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching locations:', error);
      return { success: false, error: 'Failed to fetch locations' };
    }
  },

  createLocation: async (novelId, locationData) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;

    try {
      const response = await axios.post(`${API_URL}/locations/create?userId=${userId}`, { 
        novelId, 
        location: locationData 
      });
      return response.data;
    } catch (error) {
      console.error('Error creating location:', error);
      return { success: false, error: 'Failed to create location' };
    }
  },

  updateLocation: async (locationId, locationData) => {
    try {
      const response = await axios.put(`${API_URL}/locations/${locationId}`, locationData);
      return response.data;
    } catch (error) {
      console.error('Error updating location:', error);
      return { success: false, error: 'Failed to update location' };
    }
  },

  deleteLocation: async (locationId) => {
    try {
      const response = await axios.delete(`${API_URL}/locations/${locationId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting location:', error);
      return { success: false, error: 'Failed to delete location' };
    }
  },

  generateLocations: async (novelId, count = 1, existingLocations = []) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;

    const apiKey = getApiKey();
    const response = await axios.post(`${API_URL}/locations/generate?userId=${userId}`, { 
      apiKey,
      novelId, 
      count,
      existingLocations
    });
    return response.data;
  },

  getLoreItemsByNovelId: async (novelId) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || 1;

      const response = await axios.get(`${API_URL}/lore/novel/${novelId}?userId=${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching lore items:', error);
      return { success: false, error: 'Failed to fetch lore items' };
    }
  },

  createLoreItem: async (novelId, loreItemData) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;

    try {
      const response = await axios.post(`${API_URL}/lore/create?userId=${userId}`, { 
        novelId, 
        loreItem: loreItemData 
      });
      return response.data;
    } catch (error) {
      console.error('Error creating lore item:', error);
      return { success: false, error: 'Failed to create lore item' };
    }
  },

  updateLoreItem: async (loreItemId, loreItemData) => {
    try {
      const response = await axios.put(`${API_URL}/lore/${loreItemId}`, loreItemData);
      return response.data;
    } catch (error) {
      console.error('Error updating lore item:', error);
      return { success: false, error: 'Failed to update lore item' };
    }
  },

  deleteLoreItem: async (loreItemId) => {
    try {
      const response = await axios.delete(`${API_URL}/lore/${loreItemId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting lore item:', error);
      return { success: false, error: 'Failed to delete lore item' };
    }
  },

  generateLoreItems: async (novelId, count, loreItems) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;

    try {
      const apiKey = getApiKey();
      const response = await axios.post(`${API_URL}/lore/generate?userId=${userId}`, {
        apiKey,
        novelId,
        count,
        loreItems
      });
      return response.data;
    } catch (error) {
      console.error('Error generating lore items:', error);
      return { success: false, error: 'Failed to generate lore items' };
    }
  },

  getUserData: async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/auth/user/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user data:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch user data'
      };
    }
  },

  cancelSubscription: async (email) => {
    try {
      const response = await axios.post(`${API_URL}/webhooks/stripe/cancel`, { email });
      return response.data;
    } catch (error) {
      console.error('Error canceling subscription:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to cancel subscription'
      };
    }
  },

  exportNovelDocx: async (novelId) => {
    const userId = JSON.parse(localStorage.getItem('user') || '{}').id || 1;
    
    const exportUrl = `${API_URL}/novels/${novelId}/export/docx?userId=${userId}`;
    
    const link = document.createElement('a');
    link.href = exportUrl;
    link.target = '_blank';
    link.download = 'novel.docx';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return { success: true };
  },
  
  exportNovelPdf: async (novelId) => {
    const userId = JSON.parse(localStorage.getItem('user') || '{}').id || 1;
    
    const exportUrl = `${API_URL}/novels/${novelId}/export/pdf?userId=${userId}`;
    
    const link = document.createElement('a');
    link.href = exportUrl;
    link.target = '_blank';
    link.download = 'novel.pdf';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return { success: true };
  },

  exportNovelMarkdown: async (novelId) => {
    const userId = JSON.parse(localStorage.getItem('user') || '{}').id || 1;
    
    const exportUrl = `${API_URL}/novels/${novelId}/export/markdown?userId=${userId}`;
    
    const link = document.createElement('a');
    link.href = exportUrl;
    link.target = '_blank';
    link.download = 'novel.md';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return { success: true };
  },

  getNovelDetails: async (novelId) => {
    console.log(`Fetching novel details for ID: ${novelId}`);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || 1;
      
      const response = await axios.get(`${API_URL}/novels/${novelId}?userId=${userId}`);
      console.log('API response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in getNovelDetails:', error);
      throw error;
    }
  },

  updateUserSettings: async (userId, settings) => {
    try {
      const response = await axios.put(`${API_URL}/auth/user/${userId}/settings`, { settings });
      return response.data;
    } catch (error) {
      console.error('Error updating user settings:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update user settings'
      };
    }
  },

  getNotesByNovelId: async (novelId) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || 1;

      const response = await axios.get(`${API_URL}/notes/novel/${novelId}?userId=${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching notes:', error);
      return { success: false, error: 'Failed to fetch notes' };
    }
  },

  getNoteById: async (noteId) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || 1;

      const response = await axios.get(`${API_URL}/notes/${noteId}?userId=${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching note:', error);
      return { success: false, error: 'Failed to fetch note' };
    }
  },

  createNote: async (novelId, title, content = '') => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || 1;

      const response = await axios.post(`${API_URL}/notes/create?userId=${userId}`, {
        novelId,
        title,
        content
      });
      return response.data;
    } catch (error) {
      console.error('Error creating note:', error);
      return { success: false, error: 'Failed to create note' };
    }
  },

  updateNote: async (noteId, title, content = '') => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || 1;

      const response = await axios.put(`${API_URL}/notes/${noteId}?userId=${userId}`, {
        title,
        content
      });
      return response.data;
    } catch (error) {
      console.error('Error updating note:', error);
      return { success: false, error: 'Failed to update note' };
    }
  },

  deleteNote: async (noteId) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || 1;

      const response = await axios.delete(`${API_URL}/notes/${noteId}?userId=${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting note:', error);
      return { success: false, error: 'Failed to delete note' };
    }
  },

  updateChapterDetails: async (chapterId, title, content) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || 1;
      
      const response = await axios.put(`${API_URL}/chapters/${chapterId}/details?userId=${userId}`, { 
        title,
        content
      });
      return response.data;
    } catch (error) {
      console.error('Error updating chapter details:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update chapter'
      };
    }
  },

  generateSceneSummary: async (sceneId) => {
    const apiKey = getApiKey();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 1;

    const response = await axios.post(`${API_URL}/scenes/${sceneId}/generate-summary?userId=${userId}`, { 
      apiKey
    });
    return response.data;
  },

  generateChapterSummary: async (chapterId, apiKey = null) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || 1;

      if (!apiKey) {
        apiKey = getApiKey();
      }
      
      const response = await axios.post(`${API_URL}/chapters/${chapterId}/generate-summary?userId=${userId}`, {
        apiKey
      });
      
      return response.data;
    } catch (error) {
      console.error('Error generating chapter summary:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  getUserSubscription: async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/auth/user/${userId}/subscription`);
      return response.data;
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch subscription data'
      };
    }
  },

  updateChatMessages: async (chatId, messages) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || 1;
      
      const response = await axios.put(`${API_URL}/chat/${chatId}/messages`, {
        messages,
        userId
      });
      return response.data;
    } catch (error) {
      console.error('Error updating chat messages:', error);
      throw error;
    }
  },

  generateNonFictionChapters: async (novelId, topic, style = '', otherInstructions = '') => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || 1;
      const apiKey = getApiKey();
      
      const response = await axios.post(`${API_URL}/chapters/generate-nonfiction?userId=${userId}`, {
        apiKey,
        novelId,
        topic,
        style,
        otherInstructions
      });
      
      return response.data;
    } catch (error) {
      console.error('Error generating non-fiction chapters:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to generate non-fiction chapters'
      };
    }
  },

  generateNonFictionTopics: async (novelId, chapterTitle) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || 1;
      const apiKey = getApiKey();
      
      const response = await axios.post(`${API_URL}/chapters/generate-topics?userId=${userId}`, {
        apiKey,
        novelId,
        chapterTitle
      });
      
      return response.data;
    } catch (error) {
      console.error('Error generating non-fiction topics:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to generate topics'
      };
    }
  },

  saveNonFictionChapters: async (novelId, chaptersWithTopics) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || 1;
      
      const response = await axios.post(`${API_URL}/chapters/save-nonfiction?userId=${userId}`, {
        novelId,
        chapters: chaptersWithTopics
      });
      
      return response.data;
    } catch (error) {
      console.error('Error saving non-fiction chapters with topics:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to save chapters and topics'
      };
    }
  },

  updateNovelSettings: async (novelId, settings) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || 1;
      
      const response = await axios.put(`${API_URL}/novels/${novelId}/settings?userId=${userId}`, {
        tone: settings.tone,
        direct_speech: settings.direct_speech,
        inner_monologue: settings.inner_monologue
      });
      
      return response.data;
    } catch (error) {
      console.error('Error updating novel settings:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update novel settings'
      };
    }
  },
};

export default api;
